import {
  ColDef,
  ColGroupDef,
  ColumnVO,
  FilterModel,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideGetRowsParams,
  RowGroupOpenedEvent,
} from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import AgGridTbl, {
  AG_GRID_CONSTANTS,
  agGridUtils,
  IAgGrid,
  ICase,
  IDocumentList,
  ILinkedCaseList,
  ITask,
  useConfigUpdates,
} from 'fso-fincrime-ag-grid-ui';
import {
  AppDispatch,
  authUtils,
  fetchPageGuidance,
  getWorkItemList,
  getWorkflowList,
  getTaskListAutoRefresh,
  getUser,
  IActiveRole,
  IAgGridRowsTaskRes,
  IAgGridTblPayload,
  IFullProject,
  IGridTableHeader,
  IUser,
  IUserQueryUseCase,
  IWorkflow,
  IWorkflows,
  IWorkItemRes,
  localStorageUtils,
  objectUtils,
  PermissionTypes,
  SDK_CONSTANTS,
  SDK_EVENTS_CONSTANTS,
  triggerEvt,
  useAlertsHook,
  useGetUsersByProject,
  useSseEventHook,
} from 'fso-fincrime-sdk-ui';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { API_CONSTANTS } from '../../utils/constants/api_constants';

const TaskList = (): JSX.Element => {
  const { t } = useTranslation();
  const { addAlert } = useAlertsHook();
  const dispatch = useDispatch<AppDispatch>();
  const gridRef = useRef<AgGridReact<ITask>>(null);
  const agGridRef: RefObject<IAgGrid> = useRef<IAgGrid>(null);
  const gridNode = gridRef.current;
  const listType = 'TASK_LIST';
  const projName: string = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.PROJECT_NAME
  );
  const workflowName: string = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_NAME
  );
  const workflowVersion: number = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_VERSION
  );
  const project: IFullProject = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.PROJECT
  );

  const usercanTaskUnassign = authUtils.checkUserPermissions(PermissionTypes.TASK_UNASSIGNMENT);
  const usercanFullTaskUnassign = authUtils.checkUserPermissions(
    PermissionTypes.FULL_TASK_UNASSIGNMENT
  );
  const usercanTaskAssign = authUtils.checkUserPermissions(PermissionTypes.TASK_REASSIGNMENT);
  const usercanFullTaskAssign = authUtils.checkUserPermissions(
    PermissionTypes.FULL_TASK_REASSIGNMENT
  );
  const usercanExportTask = authUtils.checkUserPermissions(
    PermissionTypes.DOWNLOAD_TASK_LIST_EXCEL
  );
  const autoRefresh = useSelector(getTaskListAutoRefresh);

  const gridOptions: GridOptions = {
    getRowId: (params: GetRowIdParams) => {
      /* All rows will have an unique taskId,
    Excpet the group rows, that rows will have always a single unique element
    in taskListColumnConfigurations
    */
      const col = params.data.taskListColumnConfigurations[0];
      const parentKeys = (params.parentKeys ?? []).join('-');
      return params.data.taskId ?? `${parentKeys}-${col.attributeName}-${col.attributeValue}`;
    },
  };

  const [columns, setColumns] = useState<(ColDef | ColGroupDef)[]>([]);
  const [columnsConfigs, setColumnsConfigs] = useState<IGridTableHeader[]>([]);
  const [pageSize, setPageSize] = useState<number>(8);
  const [pageNo, setPageNo] = useState<number>(0);
  const [gridApi, setGridApi] = useState<GridReadyEvent | undefined>(undefined);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [gridSelectedRows, setGridSelectedRows] = useState<
    ITask[] | ICase[] | ILinkedCaseList[] | IDocumentList[]
  >([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [payload, setPayload] = useState<IAgGridTblPayload | undefined>(undefined);
  const [columnsViews, setColumnsViews] = useState<IGridTableHeader[]>([]);
  const [groupByParams, setGroupByParams] = useState<ColumnVO[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [activeRole, setActiveRole] = useState<IActiveRole[]>(
    localStorageUtils.getSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.USER.ACTIVE_ROLE)
  );
  const [hoveredWorkflow, setHoveredWorkflow] = useState<string | undefined>();
  const [noRows, setNoRows] = useState<boolean>(false);
  const [workflows, setWorkflows] = useState<IWorkflows[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<IWorkflow | undefined>(undefined);

  // GraphQL query for users
  const {
    users: projectUsers,
    loading: usersLoading,
    error: usersError,
  } = useGetUsersByProject({ project: projName, useCase: IUserQueryUseCase.USER_STANDARD });

  useEffect(() => {
    if (projName) {
      dispatch(fetchPageGuidance({ projectName: projName }));
    }
  }, [dispatch, projName]);

  useEffect(() => {
    if (projName) {
      getWorkflowList(authUtils.getUserRolesByActiveRole(), projName)
        .then((workflowList) => {
          setWorkflows(workflowList);
          if (project?.workflows?.length == 1) {
            const firstWorkflow = project?.workflows?.[0];
            localStorageUtils.setSavedItem(
              SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_NAME,
              firstWorkflow?.workflowName
            );
            localStorageUtils.setSavedItem(
              SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_VERSION,
              firstWorkflow?.workflowVersion
            );
            const findWorkflow = project?.workflows?.find(
              (workflow) => workflow.workflowName === workflowName
            );
            setSelectedWorkflow(findWorkflow ?? firstWorkflow);
          } else {
            localStorageUtils.setSavedItem(
              SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_NAME,
              SDK_CONSTANTS.ALL
            );
            localStorageUtils.setSavedItem(
              SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_VERSION,
              0
            );

            setSelectedWorkflow({
              workflowName: SDK_CONSTANTS.ALL,
              workflowVersion: 0,
            });
          }
        })
        .catch(() => addAlert({ type: 'error', primaryText: t('ERROR_FETCHING') }));
    }
  }, [projName, activeRole]);

  useEffect(() => {
    setUsersList();
  }, [activeRole, projectUsers]);

  document.addEventListener(SDK_EVENTS_CONSTANTS.ACTIVE_ROLE_UPDATED, (e: Event) => {
    const customEvent = e as CustomEvent;
    setActiveRole(objectUtils.deepCopy(customEvent.detail));
  });

  const setUsersList = (): void => {
    if (usercanFullTaskUnassign || usercanFullTaskAssign) {
      if (!usersLoading && !usersError && projectUsers) {
        setUsers(projectUsers);
      } else if (usersError) {
        console.error('Error fetching users with GraphQL:', usersError);
      }
    } else {
      getUser(projName).then((user) => setUsers([user]));
    }
  };

  const getRowClass = (params: { data: { status: string | null } }): 'bold-font' | undefined => {
    if (params?.data?.status != null && (params?.data?.status).toUpperCase() == 'NEW') {
      return 'bold-font';
    }
  };

  const onRowSelected = (): void => {
    if (gridNode) {
      const { api } = gridNode;
      const selectedRows = agGridUtils.getSelectedRows<ITask>(api);
      const taskListSelectedRows = selectedRows.filter((row) => row.taskId);
      if (taskListSelectedRows) {
        setGridSelectedRows(taskListSelectedRows);
      }
    }
  };

  const getTasks = async (payload: IAgGridTblPayload): Promise<IAgGridRowsTaskRes | undefined> => {
    return await getWorkItemList(payload, listType).then((response: IWorkItemRes) => {
      if (!response.taskPage) {
        return;
      }
      return response.taskPage;
    });
  };

  const onRowGroupOpened = (event: RowGroupOpenedEvent): void => {
    event.api.refreshCells({ force: true });
  };

  // Function to fetch data (simulate an API call or data source fetch)
  const fetchData = useCallback(
    (params: IServerSideGetRowsParams, taskPayload: IAgGridTblPayload) => {
      const { request, success, fail } = params;
      const { filterModel, groupKeys } = request;
      const filter = filterModel as FilterModel;
      getTasks(taskPayload)
        .then((response: IAgGridRowsTaskRes | undefined) => {
          setPayload(structuredClone(taskPayload));
          if (response) {
            if (groupKeys.length === 0) {
              setPageNo(response.pageable.pageNumber);
              setTotalPages(response.totalPages - 1);
              setTotalElements(response.totalElements);
            }

            success({
              rowData: prepareTableData(response.content as ITask[]),
              rowCount: response.content.length,
            });
            if (!objectUtils.isEmpty(filter) && !response.content.length) {
              setPageNo(0);
            }
            if (!pageNo && !response.content.length) {
              gridApi?.api.showNoRowsOverlay();
            } else {
              gridApi?.api.hideOverlay();
            }
            setNoRows(!response.content.length);

            agGridUtils.keepGroupByTotalElements(response.groupKeyPagination);
          }
        })
        .catch(() => {
          addAlert({ type: 'error', primaryText: t('ERROR_FETCHING') });
          fail();
        })
        .finally(() => setDataLoading(false));
    },
    []
  );

  const saveNewPayload = (taskPayload: IAgGridTblPayload): void => {
    const payload = objectUtils.deepCopy(taskPayload);

    localStorageUtils.setSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.TASK_LIST_PAYLOAD, payload);
    triggerEvt(SDK_EVENTS_CONSTANTS.TASK_LIST_PAYLOAD_UPDATED, payload);
  };

  const prepareTableData = (content: ITask[]): ITask[] => {
    const data: ITask[] = [...content];
    content.forEach((row) => {
      row.taskListColumnConfigurations.forEach((col) => {
        (row as { [key: string]: any })[col.attributeName] = col.attributeValue;
      });
    });
    return data;
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams
  ): (string | { name: string; action: () => void })[] => {
    const id: string | undefined = params?.node?.data?.taskId;
    if (id) {
      return [
        {
          name: t('OPEN_IN_A_NEW_TAB'),
          action: (): void => {
            window.open(`${SDK_CONSTANTS.PAGES_URL.TASK}/${id}`, '_blank');
          },
        },
        'separator',
        ...AG_GRID_CONSTANTS.AG_GRID.CONTEXT_MENU_ITEMS,
      ];
    }
    return agGridUtils.getContextMenuItems();
  };

  const handleShowMoreClick = (
    key: string,
    childsCount: number,
    params: ICellRendererParams
  ): void => {
    if (params.node.id) {
      const groupKeys: string[] = agGridUtils.getExpandedGroupKeys(params.node.id, gridApi);
      const groupByParams: ColumnVO[] = localStorageUtils.getSavedItem(
        SDK_CONSTANTS.LOCAL_STORAGE_KEYS.AG_GRID.ROW_GROUP_COLS
      );
      const payload: IAgGridTblPayload = localStorageUtils.getSavedItem(
        SDK_CONSTANTS.LOCAL_STORAGE_KEYS.TASK_LIST_PAYLOAD
      );

      if (payload && key !== undefined && key !== null) {
        const newPayload = structuredClone(payload);
        newPayload.groupKeys = groupKeys;
        newPayload.rowGroupCols = groupByParams;
        newPayload.groupKeyPageRequest = agGridUtils.increaseGroupKeyPagination(key, childsCount);
        saveNewPayload(newPayload);
        setDataLoading(true);
        getTasks(newPayload)
          .then((res) => {
            res &&
              gridApi?.api?.applyServerSideTransaction({
                route: groupKeys,
                add: prepareTableData(res.content as ITask[]),
              });
          })
          .catch(() => {
            addAlert({ type: 'error', primaryText: t('ERROR_FETCHING') });
          })
          .finally(() => setDataLoading(false));
      }
    }
  };

  const getSelectedWorkflow = (selectedWorkflow: string): void => {
    const workflowChosen: IWorkflow[] = [];
    const workflowDetail = workflows?.find(
      (workflow: IWorkflows) => workflow.workflowName === selectedWorkflow
    );
    if (workflowDetail) {
      workflowChosen.push({
        workflowName: workflowDetail.workflowName,
        workflowVersion: workflowDetail.workflowVersion,
      });
      setSelectedWorkflow(workflowChosen[0]);
      localStorageUtils.setSavedItem(
        SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_NAME,
        workflowChosen[0]?.workflowName
      );
      localStorageUtils.setSavedItem(
        SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_VERSION,
        workflowChosen[0]?.workflowVersion
      );
    } else {
      workflows.forEach((workflow: IWorkflows) => {
        workflowChosen.push({
          workflowName: workflow.workflowName,
          workflowVersion: workflow.workflowVersion,
        });
        setSelectedWorkflow({
          workflowName: SDK_CONSTANTS.ALL,
          workflowVersion: 0,
        });
        localStorageUtils.setSavedItem(
          SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_NAME,
          SDK_CONSTANTS.ALL
        );
        localStorageUtils.setSavedItem(
          SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_VERSION,
          0
        );
      });
    }
    project.workflows = workflowChosen;
    localStorageUtils.setSavedItem(
      SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.PROJECT,
      project
    );
  };

  /**
   * This is a custom hook which is used to update the columns configuration
   */
  useConfigUpdates(
    {
      columnsConfigs,
      handleShowMoreClick,
    },
    (colConfigs: IGridTableHeader[], columns: (ColDef | ColGroupDef)[]) => {
      setColumnsViews(colConfigs);
      setColumns([...columns]);
    }
  );

  const reassignTasksList = () => {
    if (autoRefresh) {
      agGridRef?.current?.reloadGrid();
      addAlert({ primaryText: t('TASK_LIST.SSE.AUTO_REFRESH_ON.PRIMARY_TEXT'), type: 'info' });
    } else {
      addAlert({
        primaryText: t('TASK_LIST.SSE.AUTO_REFRESH_OFF.PRIMARY_TEXT'),
        secondaryText: t('TASK_LIST.SSE.AUTO_REFRESH_OFF.SECONDARY_TEXT'),
        type: 'info',
      });
    }
  };

  useSseEventHook(
    API_CONSTANTS.SSE_MANAGER.CHANNELS.TASK_REASSIGNMENT_TASKLIST,
    projName,
    true,
    reassignTasksList
  );

  return (
    <AgGridTbl
      showAssignTaskModule
      showResetModule
      showViewsModule
      showBulkSelectionModule
      showSearch
      showWorkflowSelector
      showAutoRefreshButton
      activeRole={activeRole}
      columns={columns}
      columnsConfigs={columnsConfigs}
      columnsViews={columnsViews}
      dataLoading={dataLoading}
      fetchData={fetchData}
      getContextMenuItems={getContextMenuItems}
      getRowClass={getRowClass}
      gridApi={gridApi}
      gridName={t('WORK_ITEMS')}
      gridOptions={gridOptions}
      gridRef={gridRef}
      gridSelectedRows={gridSelectedRows}
      groupByParams={groupByParams}
      listType={listType}
      noRows={noRows}
      onRowSelected={onRowSelected}
      onRowGroupOpened={onRowGroupOpened}
      pageNo={pageNo}
      pageSize={pageSize}
      payload={payload}
      setColumnsConfigs={setColumnsConfigs}
      setColumnsViews={setColumnsViews}
      setDataLoading={setDataLoading}
      setGridApi={setGridApi}
      setGridSelectedRows={setGridSelectedRows}
      setGroupByParam={setGroupByParams}
      setPageNo={setPageNo}
      setPageSize={setPageSize}
      setTotalPages={setTotalPages}
      showExportModule={usercanExportTask}
      workflowName={workflowName}
      workflowVersion={workflowVersion}
      totalElements={totalElements}
      totalPages={totalPages}
      usercanFullTaskAssign={usercanFullTaskAssign}
      usercanFullTaskUnassign={usercanFullTaskUnassign}
      usercanTaskAssign={usercanTaskAssign}
      usercanTaskUnassign={usercanTaskUnassign}
      users={users}
      workflows={workflows}
      selectedWorkflow={selectedWorkflow}
      getSelectedWorkflow={getSelectedWorkflow}
      project={project}
      hoveredWorkflow={hoveredWorkflow}
      setHoveredWorkflow={setHoveredWorkflow}
      ref={agGridRef}
    />
  );
};

export default TaskList;
